import { fetchAllContributions } from "@/services/contributions";
import {
  fetchInvestmentOptions,
  insertInvestmentOption,
} from "@/services/investmentOptions";
import { RootState } from "@/store";
import { clearShouldResetAllocations } from "@/store/wizard";
import {
  BeneficiaryInvestmentProfile,
  DropdownInvestmentOption,
  InvestmentOptionAPIType,
} from "@/types/investmentProfile";
import { handleApiError } from "@/utils/errorHandling";
import { customToast } from "@components/Common/Toast";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  calculateInvestmentOptionRecommendations,
  createRecommendedOptionsMap,
  determineInvestmentPlanType,
  filterMichiganInvestmentOptions,
  formatBeneficiariesForAPI,
  formatBeneficiariesForInvestmentProfile,
  formatInvestmentOptionsForDropdown,
  getInvestmentOptionDetails,
} from "../utils/investmentProfileUtils";

interface UseInvestmentProfileManagementReturn {
  beneficiaries: BeneficiaryInvestmentProfile[];
  setBeneficiaries: React.Dispatch<
    React.SetStateAction<BeneficiaryInvestmentProfile[]>
  >;
  investmentOptionsFromAPI: InvestmentOptionAPIType[];
  formattedInvestmentOptionsFromAPI: DropdownInvestmentOption[];
  recommendedOptions: { [beneficiaryId: string]: string };
  loading: boolean;
  isSubmitting: boolean;
  error: string | null;
  handleInitialDataSync: () => Promise<void>;
  handleCustomPlanSelection: (index: number, investmentOption: string) => void;
  handleAcademicYearSelection: (index: number, academicYear: string) => void;
  handleSubmit: (
    isUserEnrolled?: boolean,
    onCancel?: () => void,
    handleNext?: () => void,
  ) => Promise<void>;
}

export const useInvestmentProfileManagement =
  (): UseInvestmentProfileManagementReturn => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [beneficiaries, setBeneficiaries] = useState<
      BeneficiaryInvestmentProfile[]
    >([]);
    const [investmentOptionsFromAPI, setInvestmentOptionsFromAPI] = useState<
      InvestmentOptionAPIType[]
    >([]);
    const [
      formattedInvestmentOptionsFromAPI,
      setFormattedInvestmentOptionsFromAPI,
    ] = useState<DropdownInvestmentOption[]>([]);
    const [recommendedOptions, setRecommendedOptions] = useState<{
      [beneficiaryId: string]: string;
    }>({});

    const {
      id: userAccountID,
      employeeID,
      sootchyEmployerID,
    } = useSelector((state: RootState) => state.user);

    const handleInitialDataSync = async () => {
      try {
        setError(null);
        setLoading(true);

        const [investmentOptionsResponse, beneficiaryResponse] =
          await Promise.all([
            //setupMy529OtherInvestmentOptions(),
            fetchInvestmentOptions(),
            fetchAllContributions(employeeID, userAccountID),
          ]);

        const filteredInvestmentOptionsResponse =
          filterMichiganInvestmentOptions(investmentOptionsResponse.data);

        setInvestmentOptionsFromAPI(filteredInvestmentOptionsResponse);

        const formattedFilteredOptions = formatInvestmentOptionsForDropdown(
          filteredInvestmentOptionsResponse,
        );

        setFormattedInvestmentOptionsFromAPI(formattedFilteredOptions);

        const recommendations = calculateInvestmentOptionRecommendations(
          beneficiaryResponse.data,
          filteredInvestmentOptionsResponse,
        );

        const recommendedOptionsMap = createRecommendedOptionsMap(
          beneficiaryResponse.data,
          recommendations,
        );

        const formattedBeneficiaries = formatBeneficiariesForInvestmentProfile(
          beneficiaryResponse.data,
          filteredInvestmentOptionsResponse,
          recommendations,
        );

        setRecommendedOptions(recommendedOptionsMap);
        setBeneficiaries(formattedBeneficiaries);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error.statusText) {
          setError(error.statusText);
        } else {
          setError("Unable to fetch beneficiaries");
        }
      } finally {
        setLoading(false);
      }
    };

    const handleCustomPlanSelection = (
      index: number,
      investmentOption: string,
    ) => {
      const investmentDetails = getInvestmentOptionDetails(
        investmentOption,
        investmentOptionsFromAPI,
      );

      if (!investmentDetails) return;

      setBeneficiaries((prev) =>
        prev.map((b, i) =>
          i === index
            ? {
                ...b,
                selectedInvestmentOptionID: investmentDetails.portfiloShortName,
                selectedInvestmentOptionName: investmentDetails.portfolioName,
                selectedAcademicTargetYear: investmentDetails.investmentUpto,
                investmentPlanType: determineInvestmentPlanType(
                  investmentDetails.portfiloShortName,
                  recommendedOptions[b.id],
                ),
              }
            : b,
        ),
      );
    };

    const handleAcademicYearSelection = (
      index: number,
      academicYear: string,
    ) => {
      setBeneficiaries((prev) =>
        prev.map((b, i) =>
          i === index
            ? {
                ...b,
                selectedAcademicTargetYear: academicYear,

                selectedInvestmentOptionID: "",
                selectedInvestmentOptionName: "Please select an option",
                investmentPlanType: "custom",
              }
            : b,
        ),
      );
    };

    const handleSubmit = async (
      isUserEnrolled?: boolean,
      onCancel?: () => void,
      handleNext?: () => void,
    ) => {
      try {
        const incompleteSelections = beneficiaries.filter(
          (beneficiary) =>
            !beneficiary.selectedInvestmentOptionID ||
            beneficiary.selectedInvestmentOptionID === "",
        );

        if (incompleteSelections.length > 0) {
          const names = incompleteSelections
            .map((b) => b.name.split(" ")[0])
            .join(", ");
          customToast(
            `Please select investment options for ${names} to complete enrollment`,
            "error",
          );
          return;
        }

        setIsSubmitting(true);

        const formattedBeneficiariesForAPISubmission =
          formatBeneficiariesForAPI(
            beneficiaries,
            userAccountID,
            employeeID,
            sootchyEmployerID,
          );

        const response = await insertInvestmentOption(
          formattedBeneficiariesForAPISubmission,
        );

        if (response.status === 200) {
          beneficiaries.forEach((beneficiary: BeneficiaryInvestmentProfile) => {
            customToast(
              `Successfully enrolled ${
                beneficiary.name.split(" ")[0]
              } as a beneficiary`,
              "success",
            );
          });

          dispatch(clearShouldResetAllocations());

          if (isUserEnrolled && onCancel) {
            navigate("/");
          } else if (handleNext) {
            handleNext();
          }
        } else {
          customToast(
            `Unable to complete enrollment (${response.status}). Please try again.`,
            "error",
          );
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        handleApiError(
          error,
          "Unable to complete enrollment",
          true,
          customToast,
        );
      } finally {
        setIsSubmitting(false);
      }
    };

    return {
      beneficiaries,
      setBeneficiaries,
      investmentOptionsFromAPI,
      formattedInvestmentOptionsFromAPI,
      recommendedOptions,
      loading,
      isSubmitting,
      error,
      handleInitialDataSync,
      handleCustomPlanSelection,
      handleAcademicYearSelection,
      handleSubmit,
    };
  };
